import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, MapPin } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const SEOSpringPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/seo-spring');

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SEO en Spring TX",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": { "@type": "City", "name": "Spring", "containedIn": { "@type": "State", "name": "Texas" } }
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>SEO Spring TX | Posicionamiento Local en Google – Luis ROC</title>
        <meta name="description" content="Agencia SEO en Spring, TX. Mejora tu visibilidad en Google Maps y búsquedas locales en Spring. Reunión inicial gratuita con experto local." />
        <meta name="keywords" content="seo spring tx, seo spring texas, agencia seo spring, posicionamiento google spring" />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="geo.placename" content="Spring" />
      </Helmet>

      <JsonLd data={serviceSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav 
            items={[
              { name: "Servicios", url: "/servicios" },
              { name: "Localidades", url: "/servicios-locales-houston" },
              { name: "SEO Spring", url: "/seo-spring" }
            ]}
            language="es"
          />

          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              SEO en Spring TX que posiciona tu negocio en Google
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Spring, Texas es una comunidad próspera al norte de Houston con más de 62,000 habitantes. En Luis ROC 
              optimizamos tu presencia local para que aparezcas cuando tus vecinos buscan tus servicios en Google. 
              SEO local + Google Business Profile + contenido optimizado para ZIP 77373, 77379, 77386, 77388, 77389.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => window.location.href = '/diagnostico-seo-gratis'}>
                Reunión inicial gratuita de 30 minutos
              </Button>
              <Button size="lg" variant="outline" onClick={() => window.location.href = '/contacto'}>
                Hablar con Experto
              </Button>
            </div>
          </section>

          <section className="py-16 bg-accent/5 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">
              ¿Por qué SEO local en Spring?
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <Card className="p-6">
                <Check className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Mercado local competitivo</h3>
                <p className="text-muted-foreground">
                  Spring tiene alta densidad de servicios locales. Destacarte en Google Maps es crucial.
                </p>
              </Card>

              <Card className="p-6">
                <Check className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Búsquedas "cerca de mí"</h3>
                <p className="text-muted-foreground">
                  Los residentes de Spring buscan proveedores locales. Aparecer en el map pack = más llamadas.
                </p>
              </Card>

              <Card className="p-6">
                <Check className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold mb-2">ROI superior a largo plazo</h3>
                <p className="text-muted-foreground">
                  SEO local genera tráfico orgánico continuo sin pagar por cada clic.
                </p>
              </Card>
            </div>
          </section>

          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-4">Mejora tu visibilidad en Google en Spring TX</h2>
            <p className="text-xl mb-6 opacity-90">Revisión inicial y definición de siguientes pasos</p>
            <Button size="lg" variant="secondary" onClick={() => window.location.href = '/diagnostico-seo-gratis'}>
              Agendar reunión inicial gratuita de 30 minutos
            </Button>
          </section>

          <section className="py-8 text-center">
            <h3 className="text-xl font-semibold mb-4">Servicios en otras localidades</h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" onClick={() => window.location.href = '/seo-the-woodlands'}>SEO The Woodlands</Button>
              <Button variant="outline" onClick={() => window.location.href = '/seo-houston'}>SEO Houston</Button>
              <Button variant="outline" onClick={() => window.location.href = '/servicios-locales-houston'}>Ver Todas</Button>
            </div>
          </section>
        </main>

        <KnowledgeConnections path="/seo-spring" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default SEOSpringPage;
